/**
 * Layer-1 document proposal review page.
 * Route: /app/bids/:bidId/responses/proposal
 *
 * States: generating (poll-driven progress) → review (editable cards +
 * excluded strip) → per-document accept | regenerate | discard.
 * Spec: response-structure-proposal-spec.md §2; mockup screen 3.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Loader2, AlertCircle, ArrowLeft, RotateCcw, FileSearch, Sparkles } from 'lucide-react'
import { GradientSparkleIcon } from '@/components/extraction/GradientSparkleIcon'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { NOTICE_QUIET } from '@/lib/notice-tier'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { RegenerateProposalDialog } from '@/components/RegenerateProposalDialog'
import { useStructureProposalStore } from '@/stores/structure-proposal-store'
import { ProposedDocumentCard } from '@/components/structure-proposal/ProposedDocumentCard'
import { ExcludedItemsStrip } from '@/components/structure-proposal/ExcludedItemsStrip'

/**
 * The generator's own explanation of what it did (and didn't) propose. On an
 * empty bid this note IS the answer — never drop it (F5).
 *
 * Quiet tier: there is nothing to accept or dismiss here, so no accent edge.
 * Quiet is not faint — full-strength text on a neutral block stays perfectly
 * legible. It's the repetition that made the old fill unbearable: one azure
 * band per note.
 */
function ProposalNotes({ notes }: { notes: string[] }) {
  if (notes.length === 0) return null
  return (
    <div className="space-y-2">
      {notes.map((note, i) => (
        <div
          key={i}
          className={cn(NOTICE_QUIET, 'flex items-start gap-2 text-left text-sm text-foreground')}
        >
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          {note}
        </div>
      ))}
    </div>
  )
}

export function DocumentProposalPage() {
  const { bidId } = useParams<{ bidId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmRegenerateOpen, setConfirmRegenerateOpen] = useState(false)

  const {
    generation,
    documents,
    isGenerating,
    isLoading,
    isAccepting,
    error,
    isDirty,
    generate,
    load,
    setIncluded,
    updateDocument,
    accept,
    discard,
  } = useStructureProposalStore()

  useEffect(() => {
    if (!bidId) return
    void (async () => {
      // Resume an in-flight or completed (unaccepted) generation — proposals
      // persist server-side until accepted, discarded or regenerated.
      const state = useStructureProposalStore.getState()
      if (!state.generation && !state.isGenerating) {
        await load(bidId)
      }
      // Entry points navigate with ?start=1: only start a NEW generation when
      // there is nothing usable to resume (never silently supersede a
      // complete proposal the user hasn't reviewed).
      if (searchParams.get('start') === '1') {
        setSearchParams({}, { replace: true })
        const latest = useStructureProposalStore.getState()
        const resumable =
          latest.isGenerating ||
          (latest.generation && latest.generation.status === 'complete')
        if (!resumable) void generate(bidId)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidId])

  if (!bidId) return null

  const includedCount = documents.filter((d) => d.included).length
  const excludedCount = documents.length - includedCount
  const responsesUrl = `/app/bids/${bidId}/write`

  // The proposal has two sources. Checklist-derived documents carry the
  // submission requirement they came from; the criteria fallback sets
  // `submissionRequirementId: null` and says so in its own rationale
  // ("No submission checklist found — proposed from the evaluation criteria",
  // document-assembly.ts). The framing copy used to name the checklist
  // unconditionally, so on a criteria-derived proposal the page contradicted its
  // own banner three times over — subtitle, empty state and confirm dialog all
  // claiming a checklist the bid does not have.
  const fromChecklist = documents.some((d) => d.submissionRequirementId !== null)
  const sourceLabel = fromChecklist ? 'the submission checklist' : 'the evaluation criteria'

  const handleAccept = async () => {
    const artifactIds = await accept(bidId)
    if (artifactIds) {
      setConfirmOpen(false)
      void navigate(responsesUrl)
    }
  }

  const handleDiscard = async () => {
    await discard(bidId)
    void navigate(responsesUrl)
  }

  // Regenerate replaces the proposal outright. Confirm only when the user has
  // review edits to lose — a clean proposal regenerates on the first click.
  const handleRegenerateClick = () => {
    if (isDirty) {
      setConfirmRegenerateOpen(true)
      return
    }
    void generate(bidId)
  }

  const handleConfirmRegenerate = () => {
    setConfirmRegenerateOpen(false)
    void generate(bidId)
  }

  // ===================
  // Generating / loading states
  // ===================

  if (isLoading || isGenerating) {
    return (
      <div className="mx-auto flex h-full max-w-2xl flex-col items-center justify-center px-6 text-center">
        <Loader2 className="mb-4 h-8 w-8 animate-spin text-[hsl(var(--ai-accent))]" />
        <h2 className="text-base font-medium">
          {isLoading ? 'Loading proposal…' : 'Proposing your response documents…'}
        </h2>
        {isGenerating && (
          <p className="mt-2 text-sm text-muted-foreground">
            Classifying the submission checklist — which items need written responses, which are
            attachments and forms. Usually under 15 seconds.
          </p>
        )}
      </div>
    )
  }

  // ===================
  // Error / empty states
  // ===================

  if (error && documents.length === 0) {
    return (
      <div className="mx-auto flex h-full max-w-2xl flex-col items-center justify-center px-6 text-center">
        <AlertCircle className="mb-4 h-8 w-8 text-destructive" />
        <h2 className="text-base font-medium">Proposal failed</h2>
        <p className="mt-2 text-sm text-muted-foreground">{error}</p>
        <div className="mt-4 flex gap-2">
          <Button variant="ghost" onClick={() => void navigate(responsesUrl)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to responses
          </Button>
          <Button disabled={isGenerating} onClick={() => void generate(bidId)}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Try again
          </Button>
        </div>
      </div>
    )
  }

  // Completed, but the generator proposed nothing. Its notes say why (empty
  // checklist, no criteria, everything triaged to attachments) — surface them
  // and account for the excluded items rather than looking like a dead CTA.
  if (generation?.status === 'complete' && documents.length === 0) {
    const notes = generation.proposal?.notes ?? []
    const excludedItems = generation.proposal?.excludedItems ?? []
    return (
      <div className="mx-auto flex h-full max-w-2xl flex-col items-center justify-center px-6 text-center">
        <FileSearch className="mb-4 h-8 w-8 text-muted-foreground" />
        <h2 className="text-base font-medium">No response documents to propose</h2>
        {notes.length > 0 ? (
          <div className="mt-3 w-full">
            <ProposalNotes notes={notes} />
          </div>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            Nothing in this bid's submission checklist needs a written response.
          </p>
        )}
        {excludedItems.length > 0 && (
          <div className="mt-4 w-full text-left">
            <ExcludedItemsStrip items={excludedItems} />
          </div>
        )}
        <div className="mt-4 flex gap-2">
          <Button variant="ghost" onClick={() => void navigate(responsesUrl)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to responses
          </Button>
          <Button disabled={isGenerating} onClick={() => void generate(bidId)}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Try again
          </Button>
        </div>
      </div>
    )
  }

  if (documents.length === 0) {
    return (
      <div className="mx-auto flex h-full max-w-2xl flex-col items-center justify-center px-6 text-center">
        <h2 className="text-base font-medium">No proposal yet</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {/* Nothing has been generated yet, so the source is genuinely unknown
              here — name both rather than assert a checklist that may not exist. */}
          Propose response documents from this bid's submission checklist, or its evaluation
          criteria where there is no checklist.
        </p>
        <div className="mt-4 flex gap-2">
          <Button variant="ghost" onClick={() => void navigate(responsesUrl)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to responses
          </Button>
          <Button disabled={isGenerating} onClick={() => void generate(bidId)}>
            <GradientSparkleIcon className="mr-2 h-4 w-4" />
            Propose response documents
          </Button>
        </div>
      </div>
    )
  }

  // ===================
  // Review state
  // ===================

  const proposal = generation?.proposal

  return (
    <div className="h-full overflow-y-auto">
      <div className="w-full px-6 py-6">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-sm font-medium">Proposed response documents</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {documents.length} document{documents.length === 1 ? '' : 's'} from {sourceLabel} ·
            outlines are proposed inside each document after creating
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="ghost" size="sm" disabled={isGenerating} onClick={() => void handleDiscard()}>
            Discard
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={isGenerating}
            onClick={handleRegenerateClick}
          >
            <RotateCcw className="mr-1.5 h-4 w-4" />
            Regenerate
          </Button>
          <Button size="sm" disabled={includedCount === 0} onClick={() => setConfirmOpen(true)}>
            Create {includedCount} document{includedCount === 1 ? '' : 's'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {proposal && proposal.notes.length > 0 && (
        <div className="mb-4">
          <ProposalNotes notes={proposal.notes} />
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        {documents.map((doc) => (
          <ProposedDocumentCard
            key={doc.key}
            document={doc}
            onIncludedChange={(included) => setIncluded(doc.key, included)}
            onNameChange={(name) => updateDocument(doc.key, { name })}
          />
        ))}
      </div>

      {proposal && (
        <div className="mt-6">
          <ExcludedItemsStrip items={proposal.excludedItems} />
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Create {includedCount} response document{includedCount === 1 ? '' : 's'}?
            </AlertDialogTitle>
            {/* The names are on the cards directly behind this dialog — the user just
                toggled them. Re-listing them here as comma-joined prose was a wall of
                text at 8+ documents, and said nothing the page didn't already show. */}
            <AlertDialogDescription>
              {fromChecklist
                ? 'Each document will be created and linked to its submission checklist item.'
                : 'Each document will be created from the evaluation criteria — there is no submission checklist to link to.'}
              {excludedCount > 0 && (
                <>
                  {' '}
                  The {excludedCount} excluded document{excludedCount === 1 ? '' : 's'} can be
                  re-proposed later.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Back</AlertDialogCancel>
            <AlertDialogAction disabled={isAccepting} onClick={() => void handleAccept()}>
              {isAccepting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create documents
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RegenerateProposalDialog
        open={confirmRegenerateOpen}
        onOpenChange={setConfirmRegenerateOpen}
        onConfirm={handleConfirmRegenerate}
      />
      </div>
    </div>
  )
}
